import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import Mandala from "@/components/Mandala";
import { toast } from "sonner";

/**
 * Post-Stripe redirect page for reiki bookings.
 * URL: /reiki/success?session_id=cs_...
 * Polls /api/reiki/bookings/status/{session_id} until confirmed.
 */
export default function ReikiBookingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [phase, setPhase] = useState(sessionId ? "polling" : "timeout");
  const [booking, setBooking] = useState(null);
  const [paymentChoice, setPaymentChoice] = useState("");
  const [manualDetails, setManualDetails] = useState(null);
  const pollRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      pollRef.current += 1;
      try {
        const { data } = await api.get(`/reiki/bookings/status/${sessionId}`);
        if (cancelled) return;
        if (data.status === "confirmed" && data.booking) {
          setBooking(data.booking);
          setPhase("done");
          return;
        }
        if (pollRef.current >= 15) {
          setPhase("timeout");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (pollRef.current >= 15) setPhase("timeout");
        else setTimeout(tick, 2500);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  const chooseSessionPayment = async (method) => {
    if (!booking) return;
    setPaymentChoice(method);
    try {
      const { data } = await api.post(`/reiki/bookings/${booking.id}/session-payment`, { method });
      if (method === "stripe") {
        toast.success(`Charged $${data.charged_usd?.toFixed(2)} to your card`);
        setBooking({ ...booking, session_payment_method: "stripe", amount_paid_usd: booking.total_usd });
      } else {
        setManualDetails({ method, handle: data.handle, amount: data.amount_usd });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not process payment");
      setPaymentChoice("");
    }
  };

  if (phase === "polling") {
    return (
      <div className="pt-32 pb-24 min-h-[80vh] flex items-center" data-testid="reiki-polling">
        <div className="container-aviana max-w-2xl text-center">
          <Mandala size={80} variant="full" className="text-aviana-gold mx-auto mb-6 animate-spin-slow" />
          <p className="overline mb-3">Confirming your booking</p>
          <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">One moment…</h1>
          <p className="mt-6 text-aviana-muted">We're finalizing your reservation with Stripe. Please don't close this page.</p>
          <Loader2 size={20} className="text-aviana-gold mx-auto mt-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (phase === "timeout") {
    return (
      <div className="pt-32 pb-24 min-h-[80vh] flex items-center">
        <div className="container-aviana max-w-2xl text-center">
          <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Still confirming…</h1>
          <p className="mt-6 text-aviana-muted">
            Your card may have been added. Check your email — we'll send confirmation as soon as
            Stripe finishes processing.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/dashboard" className="btn-primary">View my dashboard</Link>
            <Link to="/reiki" className="btn-secondary">Back to booking</Link>
          </div>
        </div>
      </div>
    );
  }

  const outstanding = booking ? (booking.total_usd - (booking.amount_paid_usd || 0)) : 0;
  const start = booking ? new Date(booking.start_iso) : null;

  return (
    <div className="pt-32 pb-24 min-h-[80vh]" data-testid="reiki-success">
      <div className="container-aviana max-w-2xl text-center">
        <div className="relative mx-auto w-24 h-24 mb-6">
          <Mandala size={96} variant="full" className="text-aviana-gold absolute inset-0" />
          <CheckCircle2 size={32} className="text-aviana-plum absolute inset-0 m-auto" />
        </div>
        <p className="overline mb-3">Your session is held</p>
        <h1 className="font-display text-5xl md:text-6xl text-aviana-ink">You're on the schedule.</h1>
        {booking && start && (
          <p className="mt-6 text-lg text-aviana-muted">
            <span className="text-aviana-ink font-medium">{booking.tier_name}</span>
            <br />
            {start.toLocaleString([], {
              weekday: "long", month: "long", day: "numeric",
              hour: "numeric", minute: "2-digit",
            })}
            {" · "}
            {booking.duration_min} min
          </p>
        )}
      </div>

      {/* Session payment picker — only for non-group bookings that still have a balance */}
      {booking && outstanding > 0 && !manualDetails && (
        <div className="container-aviana max-w-2xl mt-14" data-testid="reiki-session-payment">
          <div className="border-t border-aviana-ink/10 pt-10">
            <h2 className="font-display text-3xl text-aviana-ink text-center">
              How would you like to pay ${outstanding}?
            </h2>
            <p className="mt-3 text-center text-aviana-muted text-sm">
              Your card is on file for the cancellation policy — but you can choose a
              lower-fee option below for the session cost itself.
            </p>
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <button
                onClick={() => chooseSessionPayment("stripe")}
                disabled={!!paymentChoice}
                className="border border-aviana-ink/10 rounded-2xl p-5 hover:border-aviana-gold hover:bg-white transition-all text-left"
                data-testid="reiki-pay-stripe"
              >
                <div className="font-display text-xl text-aviana-ink mb-1">Stripe</div>
                <div className="text-sm text-aviana-muted">Charge the card on file now.</div>
                {paymentChoice === "stripe" && (
                  <Loader2 size={14} className="mt-2 animate-spin text-aviana-gold" />
                )}
              </button>
              <button
                onClick={() => chooseSessionPayment("venmo")}
                disabled={!!paymentChoice}
                className="border border-aviana-ink/10 rounded-2xl p-5 hover:border-aviana-gold hover:bg-white transition-all text-left"
                data-testid="reiki-pay-venmo"
              >
                <div className="font-display text-xl text-aviana-ink mb-1">Venmo</div>
                <div className="text-sm text-aviana-muted">Pay by phone, no processing fee.</div>
              </button>
              <button
                onClick={() => chooseSessionPayment("zelle")}
                disabled={!!paymentChoice}
                className="border border-aviana-ink/10 rounded-2xl p-5 hover:border-aviana-gold hover:bg-white transition-all text-left"
                data-testid="reiki-pay-zelle"
              >
                <div className="font-display text-xl text-aviana-ink mb-1">Zelle</div>
                <div className="text-sm text-aviana-muted">Bank-to-bank, no fees.</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {manualDetails && (
        <div className="container-aviana max-w-2xl mt-10">
          <div className="border border-aviana-gold/40 rounded-2xl p-6 md:p-8 bg-aviana-cream/30 text-center">
            <p className="overline mb-2">Payment instructions</p>
            <h3 className="font-display text-2xl text-aviana-ink">
              Send ${manualDetails.amount} via {manualDetails.method}
            </h3>
            <p className="mt-3 font-mono text-aviana-plum text-lg" data-testid="reiki-manual-handle">
              {manualDetails.handle}
            </p>
            <p className="mt-4 text-sm text-aviana-muted">
              Holly will confirm receipt within 24 hours. Your card stays on file for the
              cancellation policy but won't be charged.
            </p>
          </div>
        </div>
      )}

      <div className="container-aviana max-w-2xl text-center mt-12">
        <p className="text-sm text-aviana-muted">
          A confirmation email with prep instructions is on its way.
        </p>
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/dashboard" className="btn-primary" data-testid="reiki-dashboard-link">View my dashboard</Link>
          <Link to="/reiki" className="btn-secondary">Book another session</Link>
        </div>
      </div>
    </div>
  );
}
